import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';

import Validator from '../../../shared/validator';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import Loading from '../../../shared/loading';
import mutate from '../../../shared/mutate';
import { EMAIL_REGEX, URL_REGEX } from '../../../shared/consts';
import Partnership from './partnership';
import AccountManagerControl from './account-manager-control';
import CompanySearchControl from './company-search-control';
import { getById } from '../../../services/lwadmin/partnership/partnership-service';

export default class EditPartnershipModal extends React.Component {
  static propTypes = {
    partnershipDidSave: PropTypes.func,
  }

  static defaultProps = {
    partnershipDidSave: null,
  }

  constructor(props, context) {
    super(props, context);

    this.validator = new Validator({
      nameRequired: s => !!s.partnership.name,
      contactEmailRequired: s => !!s.partnership.contact_email,
      contactEmailFormatted:
        s => !s.partnership.contact_email || EMAIL_REGEX.test(s.partnership.contact_email),
      dashboardUrlFormatted:
        s => !s.partnership.dashboard_url || URL_REGEX.test(s.partnership.dashboard_url),
    });
    this.state = {
      partnership: new Partnership(),
      validations: this.validator.getValidations(),
    };
  }

  componentDidMount() {
    const self = this;

    this.$modal.click(function onClick(e) {
      if (e.target === this) self.dismiss();
    });
  }

  handleNameChange(e) {
    const name = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.partnership.name = name;
      state.validations.nameRequiredValid = this.validator.validateNameRequired(state);
    }));
  }

  handleActiveChange(e) {
    const active = e.target.checked;

    this.setState(state => update(state, {
      partnership: {
        active: { $set: active },
      },
    }));
  }

  handleAccountManagerChange(d) {
    this.setState(state => update(state, {
      partnership: {
        account_manager: { $set: d },
      },
    }));
  }

  handlePartnershipTypeChange(e) {
    const partnershipType = e.target.value;

    this.setState(state => update(state, {
      partnership: {
        partnership_type: { $set: partnershipType },
      },
    }));
  }

  handleSendAutomaticallyChange(e) {
    const sendAutomatically = e.target.checked;

    this.setState(state => update(state, {
      partnership: {
        send_automatically: { $set: sendAutomatically },
      },
    }));
  }

  handleContactEmailChange(e) {
    const contactEmail = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.partnership.contact_email = contactEmail;
      state.validations.contactEmailRequiredValid = this
        .validator.validateContactEmailRequired(state);
      state.validations.contactEmailFormattedValid = this
        .validator.validateContactEmailFormatted(state);
    }));
  }

  handleDashboardUrlChange(e) {
    const dashboardUrl = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.partnership.dashboard_url = dashboardUrl;
      state.validations.dashboardUrlFormattedValid = this
        .validator.validateDashboardUrlFormatted(state);
    }));
  }

  handleNewCompanySelected(company) {
    this.setState(state => update(state, {
      partnership: {
        companies: {
          $push: [company],
        },
      },
    }));
  }

  save() {
    this.setState(
      state => ({
        validations: this.validator.validateForm(state),
      }),
      () => {
        if (!this.validator.formValid) return;

        this.loading.start();
        this.state.partnership
          .save()
          .then(result => {
            this.dismiss();
            this.props.partnershipDidSave
              && this.props.partnershipDidSave(new Partnership(result.data));
          })
          .catch(error => defaultOnErrorHandler(error, 'Error saving partnership'))
          .finally(() => this.loading.stop());
      },
    );
  }

  present(partnershipId = null) {
    this.validator.resetValidation();
    this.setState({ validations: this.validator.getValidations() });
    this.$overlay.fadeIn();
    this.$modal.fadeIn();
    this.$partnershipNameInput.focus();

    if (partnershipId === null) {
      this.setState({ partnership: new Partnership() });
    } else {
      this.loading.start();
      getById(partnershipId)
        .then(result => this.setState({ partnership: new Partnership(result.data) }))
        .catch(error => defaultOnErrorHandler(error, 'Error loading partnership'))
        .finally(() => this.loading.stop());
    }
  }

  dismiss() {
    this.$overlay.fadeOut();
    this.$modal.fadeOut();
  }

  activateContributor(e, contributor) {
    e && e.preventDefault && e.preventDefault();

    this.setState(state => update(state, {
      partnership: {
        contributors: {
          [_(state.partnership.contributors).indexOf(contributor)]: {
            active: { $set: true },
          },
        },
      },
    }));
  }

  removeCompany(e, company) {
    e && e.preventDefault && e.preventDefault();

    this.setState(state => {
      state.partnership.companies = _.without(state.partnership.companies, company);
      return state;
    });
  }

  deactivateContributor(e, contributor) {
    e && e.preventDefault && e.preventDefault();

    this.setState(state => update(state, {
      partnership: {
        contributors: {
          [_(state.partnership.contributors).indexOf(contributor)]: {
            active: { $set: false },
          },
        },
      },
    }));
  }

  filterExistingCompanies(companies) {
    return companies
      .filter(c => _.every(this.state.partnership.companies, e => e.id !== c.id));
  }

  renderContributors() {
    const list = this.state.partnership.contributors;
    if (!list.length) {
      return <p>No contributors</p>;
    }

    return (
      <ul>
        {list.map(c => (
          <li key={c.id}>
            {c.active ? <span>{c.name}</span> : <strike>{c.name}</strike>}
            <span>
              {' ('}
              <a
                href="#"
                onClick={e => this[`${c.active ? 'de' : ''}activateContributor`](e, c)}
              >
                {c.active ? 'Deactivate' : 'Activate'}
              </a>
              )
            </span>
          </li>
        ))}
      </ul>
    );
  }

  renderCompanies() {
    const list = this.state.partnership.companies;
    if (!list.length) {
      return <p>No companies</p>;
    }

    return (
      <ul>
        {list.map(c => (
          <li key={c.id}>
            <span>{c.name}</span>
            <span>
              &nbsp;
              <a href="#" onClick={e => this.removeCompany(e, c)}>
                Remove
              </a>
            </span>
          </li>
        ))}
      </ul>
    );
  }

  render() {
    return (
      <div>
        <div className="modal-overlay" role="region" ref={el => (this.$overlay = $(el))} onClick={() => this.dismiss()} />
        <div className="modal --large" tabIndex="-1" role="dialog" ref={el => (this.$modal = $(el))}>
          <article>
            <header>
              <h4>
                <span>{`${this.state.partnership.id ? 'Editing' : 'Creating'} a partnership`}</span>
              </h4>
            </header>
            <Loading ref={c => (this.loading = c)}>
              <section className="modal-columnar-container">
                <section>
                  <p className={`form-group ${this.state.validations.nameRequiredValid ? '' : 'has-error'}`}>
                    Partnership name
                    <input
                      id="partnership-name"
                      type="text"
                      placeholder="Partnership name"
                      ref={el => (this.$partnershipNameInput = $(el))}
                      value={this.state.partnership.name}
                      onChange={e => this.handleNameChange(e)}
                    />
                    {this.state.validations.nameRequiredValid || (
                      <span className="help-block">Partnership name is required</span>
                    )}
                  </p>
                  <p className="form-group">
                    <label htmlFor="active">
                      <input
                        id="active"
                        type="checkbox"
                        checked={this.state.partnership.active}
                        onChange={e => this.handleActiveChange(e)}
                      />
                      Active
                    </label>
                  </p>
                  <div className="form-group">
                    <AccountManagerControl
                      accountManagerId={_.get(this.state.partnership, 'account_manager.id', null)}
                      onSelected={e => this.handleAccountManagerChange(e)}
                    />
                  </div>
                  <p className="form-group">
                    Partnership type
                    <select
                      id="types"
                      value={this.state.partnership.partnership_type}
                      onChange={e => this.handlePartnershipTypeChange(e)}
                    >
                      <option value="Silver" key="Silver">Silver</option>
                      <option value="Gold" key="Gold">Gold</option>
                      <option value="Platinum" key="Platinum">Platinum</option>
                    </select>
                  </p>
                  <p className="form-group">
                    <label htmlFor="auto-send-lead">
                      <input
                        id="auto-send-lead"
                        type="checkbox"
                        checked={this.state.partnership.send_automatically}
                        onChange={e => this.handleSendAutomaticallyChange(e)}
                      />
                      Send lead contacts automatically
                    </label>
                  </p>
                  <p className={`form-group ${this.state.validations.contactEmailRequiredValid && this.state.validations.contactEmailFormattedValid ? '' : 'has-error'}`}>
                    Send contact emails to
                    <input
                      id="email"
                      type="email"
                      placeholder="Contact email"
                      value={this.state.partnership.contact_email}
                      onChange={e => this.handleContactEmailChange(e)}
                      disabled={!this.state.partnership.send_automatically}
                      readOnly={!this.state.partnership.send_automatically}
                    />
                    {this.state.validations.contactEmailRequiredValid || <span className="help-block">Contact email is required</span>}
                    {this.state.validations.contactEmailFormattedValid || <span className="help-block">Contact email must be in the correct format</span>}
                  </p>
                  <p className={`form-group ${this.state.validations.dashboardUrlFormattedValid ? '' : 'has-error'}`}>
                    Dashboard URL
                    <input
                      id="dashboard-url"
                      type="text"
                      placeholder=""
                      value={this.state.partnership.dashboard_url}
                      onChange={e => this.handleDashboardUrlChange(e)}
                    />
                    {this.state.validations.dashboardUrlFormattedValid || <span className="help-block">Dashboard URL is not valid</span>}
                  </p>
                </section>
                <section>
                  <div className="form-group">
                    <CompanySearchControl
                      filterExisting={companies => this.filterExistingCompanies(companies)}
                      onCompanySelected={company => this.handleNewCompanySelected(company)}
                    />
                    {this.renderContributors()}
                    {this.renderCompanies()}
                  </div>
                </section>
              </section>
              <footer>
                <button type="button" className="btn btn-secondary" onClick={() => this.dismiss()}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={() => this.save()}>Save</button>
              </footer>
            </Loading>
          </article>
        </div>
      </div>
    );
  }
}
